/**
 * areas.ts — the Production Settings "Console" area registry.
 *
 * Single source of truth for the seven settings areas: rail order, icons,
 * per-section metadata (search index + scroll anchors), and the legacy
 * `?tab=` / hash deep-link mapping. Panels and the rail both render from
 * this registry so a section can never exist in one and be missing from
 * the other.
 *
 * Scope vocabulary (drives <ScopeBadge>):
 * - 'location' — persisted per-location (locations.production_settings JSONB,
 *   direct locations.* columns, or the 3PL junction). Edits bind to the
 *   header LocationFilter selection.
 * - 'org'      — persisted org-wide (prod_settings row / Vault credentials).
 * - 'browser'  — persisted in this browser's localStorage only.
 */
import i18next from 'i18next';
import type { LucideIcon } from 'lucide-react';
import {
  MapPin,
  PackageSearch,
  Workflow,
  Printer,
  Tag,
  BellRing,
  Activity,
} from 'lucide-react';

export type SettingScope = 'location' | 'org' | 'browser';

export type SettingsAreaId =
  | 'locations'
  | 'orders'
  | 'workflow'
  | 'printing'
  | 'labels-qr'
  | 'alerts'
  | 'system';

export interface SettingsSectionMeta {
  /** DOM anchor id within the panel (also the search-jump target). */
  id: string;
  title: string;
  description: string;
  /** Extra search terms beyond the title (lowercase). */
  keywords?: string[];
  /** Dominant persistence scope, shown as a badge on the section header. */
  scope?: SettingScope;
  /** Admin-only sections are hidden from non-admin search results too. */
  adminOnly?: boolean;
}

export interface SettingsArea {
  id: SettingsAreaId;
  title: string;
  description: string;
  icon: LucideIcon;
  /** True when the area edits per-location state → show the Editing chip. */
  perLocation?: boolean;
  /** True for the diagnostics area — status/actions, not configuration. */
  diagnostics?: boolean;
  sections: SettingsSectionMeta[];
}

export const SETTINGS_AREAS: SettingsArea[] = [
  {
    id: 'locations',
    title: 'Locations & Fulfilment',
    description:
      'Where the selected location prints, ships and invoices, plus carrier accounts and 3PL client overrides.',
    icon: MapPin,
    perLocation: true,
    sections: [
      {
        id: 'location',
        title: 'Location',
        description: 'Production enablement and print-file source for this location.',
        keywords: ['production enabled', 'print file source', 'tencent region', 'gcs', 'azure', 'cos'],
        scope: 'location',
      },
      {
        id: 'shipping-provider',
        title: 'Shipping & invoicing',
        description: 'Shipping provider, default print technology, and packing invoices.',
        keywords: ['starshipit', 'portless', 'yunexpress', 'manual', 'dtg', 'dtf', 'invoice', 'packing slip', 'print technology', 'fulfillment writeback', 'client alias', '3pl'],
        scope: 'location',
      },
      {
        id: 'label-printer',
        title: 'Label printer',
        description: 'OS printer name the print agent uses for shipping labels.',
        keywords: ['zebra', 'printer name'],
        scope: 'location',
      },
      {
        id: 'fiery-hot-folder',
        title: 'Fiery hot folder',
        description: 'Watched folder the DTF Fiery RIP consumes at this location.',
        keywords: ['dtf', 'rip', 'hot folder', 'cadlink'],
        scope: 'location',
      },
      {
        id: 'starshipit',
        title: 'StarShipIt',
        description: 'Enable StarShipIt and store its API credentials.',
        keywords: ['shipping', 'labels', 'api key', 'subscription key', 'carrier'],
        scope: 'org',
      },
      {
        id: 'portless',
        title: 'Portless',
        description: 'Pipe17 API key and manifest settings for Portless fulfilment.',
        keywords: ['pipe17', 'cross-dock', 'first mile', 'manifest', 'china'],
        scope: 'org',
      },
      {
        // Same `settings` key as the section header, so search and the card
        // never describe different things.
        id: 'yunexpress',
        title: 'YunExpress',
        get description() { return i18next.t('production.yunexpress.description', { ns: 'settings' }); },
        keywords: ['yunexpress', 'china', 'app id', 'app secret', 'source key', 'waybill', 'shipping product', 'prefetch'],
        scope: 'org',
      },
      {
        // Same `settings` keys as the card header in LocationsFulfilmentPanel,
        // so the search result and the card never describe different cards.
        id: 'default-weights',
        get title() { return i18next.t('production.defaultWeights.title', { ns: 'settings' }); },
        get description() { return i18next.t('production.defaultWeights.description', { ns: 'settings' }); },
        keywords: ['weight', 'kg', 'default weight', 'shipping weight', 'yunexpress', 'product type', 'client', '3pl'],
        scope: 'org',
      },
    ],
  },
  {
    id: 'orders',
    title: 'Orders & Routing',
    description:
      'How Shopify orders enter production, how the queue displays them, and how they split, batch and route.',
    icon: PackageSearch,
    perLocation: true,
    sections: [
      {
        id: 'order-queue',
        title: 'Order queue',
        description: 'Queue display mode, page size and queue tag filters.',
        keywords: ['pagination', 'endless', 'orders per page'],
        scope: 'org',
      },
      {
        // Same `settings` keys as the card header in OrdersRoutingPanel.
        id: 'orders-default-client',
        get title() { return i18next.t('production.ordersDefaultClient.title', { ns: 'settings' }); },
        get description() { return i18next.t('production.ordersDefaultClient.description', { ns: 'settings' }); },
        keywords: ['client', 'clients', 'default', 'orders page', 'opens on', 'all clients', 'batch view', '3pl'],
        scope: 'location',
      },
      {
        id: 'express',
        get title() { return i18next.t('express.title', { ns: 'settings' }); },
        get description() { return i18next.t('express.sectionDescription', { ns: 'settings' }); },
        keywords: ['express', 'startrack', 'shipping', 'title', 'code', 'tags', 'recompute'],
        scope: 'org',
      },
      {
        id: 'dtf-orders',
        get title() { return i18next.t('dtfOrderTags.title', { ns: 'settings' }); },
        get description() { return i18next.t('dtfOrderTags.sectionDescription', { ns: 'settings' }); },
        keywords: ['dtf', 'dtg', 'film', 'merch', 'tags', 'print technology'],
        scope: 'org',
      },
      {
        id: 'batch-buttons',
        title: 'Batch buttons',
        description: 'One-press batch groups for the Orders page, by order tag or priority.',
        keywords: ['batch', 'express', 'startrack', 'scope', 'create batch', 'one press'],
        scope: 'org',
      },
      {
        id: 'tag-filters',
        title: 'Intake tag filters',
        description: 'Include/exclude Shopify tags that gate orders into production.',
        keywords: ['include tags', 'exclude tags', 'wholesale', 'filter'],
        scope: 'org',
      },
      {
        id: 'line-item-exclusions',
        title: 'Line-item exclusions',
        description: 'Skip Shopify line items by tag, product type, product or variant.',
        keywords: ['skip', 'exclude', 'variant', 'product type'],
        scope: 'org',
        adminOnly: true,
      },
      {
        id: 'split-orders',
        title: 'Split orders',
        description: 'Auto-accept split orders that carry configured Shopify tags.',
        keywords: ['auto accept', 'split'],
        scope: 'org',
      },
      {
        id: 'bench-routing',
        title: 'Bench routing',
        description: 'Route B2B and large orders to a work bench instead of a bin.',
        keywords: ['b2b', 'bench', 'threshold', 'large orders'],
        scope: 'org',
      },
      {
        id: 'product-type-aliases',
        title: 'Product type aliases',
        description: 'Remap Shopify product types to custom picklist display names.',
        keywords: ['alias', 'rename', 'picklist'],
        scope: 'org',
      },
      {
        id: 'shopify-tagging',
        title: 'Shopify order tagging',
        description: 'Write production status back to Shopify as order tags.',
        keywords: ['erp:in-production', 'erp:shipped', 'printrun:in-production', 'printrun:shipped', 'write_orders'],
        scope: 'org',
      },
    ],
  },
  {
    id: 'workflow',
    title: 'Production Workflow',
    description:
      'Floor rules and station behaviour: OOS handling, bins, picking, packing, hotkeys and view preferences.',
    icon: Workflow,
    perLocation: true,
    sections: [
      {
        id: 'workflow-rules',
        title: 'Workflow rules',
        description: 'Out-of-stock handling, bin confirmation, and the manual batch cap.',
        keywords: ['oos', 'hold in bin', 'oos bin', 'trust', 'confirm', 'maximum batch size'],
        scope: 'org',
      },
      {
        id: 'stations',
        title: 'Stations',
        description: 'Pretreat, packer priority, default item verification and DTF stations.',
        keywords: ['pretreat', 'packer priority', 'skip verification', 'dtf', 'film'],
        scope: 'org',
      },
      {
        id: 'picking',
        title: 'Picking',
        description: 'Picking mode, auto-batch default size, verification and scanner.',
        keywords: ['batch pick', 'pick by line', 'auto-batch', 'verification', 'scanner'],
        scope: 'browser',
      },
      {
        id: 'picklist-order',
        title: 'Picklist order',
        description: 'The order pickers walk the bays, on screen and on the printed sheet.',
        keywords: [
          'sort',
          'pickface',
          'bay',
          'style',
          'product type',
          'type',
          'colour',
          'color',
          'size',
          'order',
          'custom',
        ],
        scope: 'location',
      },
      {
        id: 'order-hold',
        title: 'Order editing hold',
        description: 'Hold new orders ~2h before batching so customers can still edit in Shopify.',
        keywords: ['2 hour', '2-hour', 'held', 'hold', 'editing window', 'batch', 'grace'],
        scope: 'location',
      },
      {
        id: 'portless-batch-gate',
        title: 'Portless label gate',
        description: 'Hold orders out of Ready to Batch until Portless has issued a shipping label.',
        keywords: ['portless', 'label', 'gate', 'batch', 'hold', 'shipping'],
        scope: 'location',
      },
      {
        id: 'preprint-permission',
        title: 'Pre-printing',
        description:
          'Which locations may pre-print garments, add pre-printed stock, or print stock films.',
        keywords: [
          'pre-print',
          'preprint',
          'pre-printed',
          'film',
          'films',
          'stock film',
          'dtf',
          'pap',
          'permission',
        ],
        scope: 'location',
      },
      {
        id: 'packing-stations',
        title: 'Packing stations',
        description: 'Packing station instances and routing rules.',
        keywords: ['packing', 'station'],
        scope: 'org',
      },
      {
        id: 'bins',
        title: 'Bin management',
        description: 'Create and manage production bins for the selected location.',
        keywords: ['bins', 'create bins'],
        scope: 'location',
      },
      {
        id: 'hotkeys',
        title: 'Hotkeys',
        description: 'Keyboard shortcuts for production station actions.',
        keywords: ['keyboard', 'shortcuts'],
        scope: 'org',
      },
      {
        id: 'issue-tabs',
        title: 'Issue tabs',
        description: 'Show or hide tabs on the Production Issues page.',
        keywords: ['issues', 'tabs', 'view preference'],
        scope: 'browser',
      },
    ],
  },
  {
    id: 'printing',
    title: 'Printing & Files',
    description:
      'The artwork pipeline: print agent, cloud storage, RIP routing, self-heal and issue/redo reasons.',
    icon: Printer,
    perLocation: true,
    sections: [
      {
        id: 'print-agent',
        title: 'Print agent',
        // BLU-877: this card now also lists the printers registered at this
        // location and the file type each one declared, which is where the
        // retired "Print file formats" card's question moved to. Its still-true
        // keywords came with it — an operator typing `kora`, `arx4` or
        // `format` must land on the surface that now owns the answer.
        description:
          'Desktop bridge for DTG printing and shipping labels, and the printers registered at this location.',
        keywords: [
          'download',
          'installer',
          'port',
          'agent',
          'printer',
          'printers',
          'format',
          'file format',
          'file type',
          'extension',
          'arx4',
          'png',
          'kora',
          'brother',
          'rip',
          'dtg',
        ],
        scope: 'browser',
      },
      {
        id: 'gcs',
        title: 'Google Cloud Storage',
        description: 'Bring-your-own GCS bucket for print files.',
        keywords: ['byo', 'bucket', 'service account'],
        scope: 'org',
      },
      {
        id: 'tencent-cos',
        title: 'Tencent Cloud Storage',
        description: 'Mainland Tencent COS credentials for sub-5s print fetches in China.',
        keywords: ['cos', 'china', 'secretid', 'secretkey', 'bucket', 'region'],
        scope: 'org',
      },
      {
        id: 'colour-codes',
        title: 'Colour codes',
        description: 'PixelWrangler colour-code alias map used by the bridge.',
        keywords: ['pixelwrangler', 'bridge', 'alias'],
        scope: 'org',
      },
      {
        id: 'rip-routing',
        title: 'RIP routing',
        // Kept in lockstep with the card header, which renders the translated
        // `production.advanced.ripRoutingDesc` (same EN wording).
        description:
          'Route each POD colour to a RIP profile folder and each size to a platen, and set the print file name code the stations look for.',
        keywords: ['rip', 'routing', 'bridge', 'platen', 'profile folder', 'file name code'],
        scope: 'org',
      },
      {
        id: 'platen-size',
        title: 'Platen size',
        // Kept in lockstep with the card header, which renders the translated
        // `platenSize.title` / `platenSize.description` (same EN wording).
        // The header's "Platen variants" is a link; this is its words as
        // plain text.
        description:
          "Which platen and platen variant (Small or Large) each size prints on, by product type. Each platen's Small and Large are set in Platen variants.",
        keywords: ['platen', 'platen variant', 'small', 'large', '10x12', '14x16', '16x18', 'size', 'print file', 'bulk', 'select all', 'apply to all sizes'],
        scope: 'org',
      },
      {
        id: 'platen-variants',
        title: 'Platen variants',
        // Kept in lockstep with the card header, which renders the translated
        // `platenVariants.title` / `platenVariants.description` (same EN wording).
        description:
          'What Large and Small are on each platen: the pixels of each print file, and how Small is made.',
        keywords: [
          'platen',
          'platen variant',
          'pixels',
          'px',
          'small',
          'large',
          'default for small',
          'aspect',
          '10x12',
          '14x16',
          '16x18',
          'made from large',
          'uploaded by hand',
        ],
        scope: 'org',
      },
      {
        id: 'print-file-reuse',
        title: 'Always their own print file',
        // Kept in lockstep with the card header, which renders the translated
        // `printFileReuse.title` / `printFileReuse.description` (same EN wording).
        description: 'These product types always get their own print file, even when the design ID matches.',
        keywords: ['print file reuse', 'reuse', 'design ID', 'own file', 'tote', 'mug', 'exclude', 'upload studio', 'shared position'],
        scope: 'org',
      },
      {
        id: 'print-size-rules',
        title: 'Print size rules',
        // Kept in lockstep with the card header in PrintingFilesPanel
        // (hard-coded English there too), word for word.
        description:
          'Per-client print dimensions (cm) by product type and size: overrides incoming artwork size. Not used for platen files.',
        keywords: ['print size', 'cm', 'dimensions', 'resize', 'client'],
        scope: 'org',
      },
      {
        // Same `settings` keys as the card header (DeclaredStoreSkusSection
        // renders its own SettingsSection), so search and the card never
        // describe different cards.
        id: 'store-skus',
        get title() { return i18next.t('storeSkus.title', { ns: 'settings' }); },
        get description() { return i18next.t('storeSkus.description', { ns: 'settings' }); },
        keywords: ['store sku', 'standard', 'standards', 'print sku', 'per-size', 'per size', 'platen', 'set up', 'verify'],
        scope: 'org',
      },
      {
        id: 'film-cut-color',
        title: 'Film trim-line colour',
        description: 'The colour the cutting machine detects and cuts along on DTF film headers.',
        keywords: ['film', 'cut', 'trim', 'laser', 'cutter', 'colour', 'color', 'dtf'],
        scope: 'location',
      },
      {
        id: 'print-file-self-heal',
        title: 'Print-file self-heal',
        // Search results truncate this; the full, visible section description
        // lives in PrintingFilesPanel — the two are a documented pair and must
        // be changed together. BLU-877: the standards auto-heal toggle moved
        // into this card from the retired "Print file formats" card, so this
        // line and its keywords absorbed that half. Without it an operator
        // hunting "recover standard products" has no path to the toggle at all.
        description:
          "Detect and recover missing print files at batch time, including standard files recovered from the product's artwork links.",
        keywords: [
          'self heal',
          'missing files',
          'slack',
          'notifications',
          'print station',
          'file check',
          'scan',
          'trust',
          'manifest',
          'production manifest',
          'faster scans',
          'standards',
          'recover',
          'artwork',
          'heal',
        ],
        scope: 'location',
      },
      {
        id: 'designs-source',
        title: 'Designs source',
        description: 'Google Drive designs folders by product type, with a default for all other products, for hands-off self-heal.',
        keywords: ['google drive', 'folder', 'product type', 'override'],
        scope: 'org',
      },
      {
        id: 'print-file-issue-reasons',
        title: 'Print-file issue reasons',
        description: 'Reasons available at the printer station when flagging a file.',
        keywords: ['issue reasons', 'printer station'],
        scope: 'org',
      },
      {
        id: 'redo-reasons',
        title: 'Redo reasons',
        description: 'Reasons available at the catcher station when flagging a redo.',
        keywords: ['redo', 'catcher'],
        scope: 'org',
      },
    ],
  },
  {
    id: 'labels-qr',
    title: 'Labels & QR',
    description:
      'Garment label size and layout, test prints, and bin / packing-shelf QR code sheets.',
    icon: Tag,
    sections: [
      {
        id: 'label-size',
        title: 'Label size',
        description: 'Default shipping-label dimensions and custom sizes.',
        keywords: ['4x3', '4x6', 'label size'],
        scope: 'org',
      },
      {
        id: 'label-layout',
        title: 'Label layout editor',
        description: 'Drag-and-drop editor for the garment label.',
        keywords: ['layout', 'editor', 'garment label'],
        scope: 'org',
      },
      {
        id: 'test-print',
        title: 'Test print',
        description: 'Print a sample garment label to check alignment.',
        keywords: ['sample', 'test label'],
        scope: 'org',
      },
      {
        id: 'bin-qr',
        title: 'Bin QR codes',
        description: 'Generate and print QR code sheets for production bins.',
        keywords: ['qr', 'bins', 'print sheet'],
        scope: 'location',
      },
      {
        id: 'shelf-qr',
        title: 'Packing shelf QR',
        description: 'Generate QR codes for packing shelves.',
        keywords: ['qr', 'shelf', 'packing'],
        scope: 'location',
      },
      {
        id: 'film-storage-qr',
        title: 'Film storage QR',
        description: 'Print QR labels for DTF pigeon holes and shelves.',
        keywords: ['qr', 'film', 'dtf', 'pigeon hole', 'shelf', 'storage'],
        scope: 'location',
      },
    ],
  },
  {
    id: 'alerts',
    title: 'Integrations & Alerts',
    description:
      'Outbound notifications and the thresholds that trigger them: Slack, customer emails, floor alerts.',
    icon: BellRing,
    perLocation: true,
    sections: [
      {
        id: 'slack',
        title: 'Slack notifications',
        description: 'Post inventory-error alerts to a Slack webhook.',
        keywords: ['webhook', 'inventory error', 'template'],
        scope: 'org',
      },
      {
        id: 'cx-notifications',
        title: 'Customer notifications',
        description: 'Emails sent to customers on production issues, plus remedy options.',
        keywords: ['cx', 'email', 'remedies', 'oos'],
        scope: 'org',
      },
      {
        id: 'alert-thresholds',
        title: 'Alert thresholds',
        description: 'Defect, inventory-error, P&P damage and stuck-bin alert levels.',
        keywords: ['defect rate', 'stuck bins', 'damage rate', 'thresholds'],
        scope: 'location',
      },
    ],
  },
  {
    id: 'system',
    title: 'System & Diagnostics',
    description:
      'Live status surfaces and destructive actions, not configuration.',
    icon: Activity,
    diagnostics: true,
    sections: [
      {
        id: 'sync-status',
        title: 'Order sync',
        description: 'Shopify order-sync health, delta compare, and manual sync trigger.',
        keywords: ['sync', 'shopify', 'run sync now', 'delta'],
      },
      {
        id: 'sentry',
        title: 'Error monitoring',
        description: 'Sentry connection status and setup guide.',
        keywords: ['sentry', 'errors', 'monitoring'],
      },
      {
        id: 'dev-tools',
        title: 'Developer tools',
        description: 'QR test panel and other development helpers.',
        keywords: ['qr test panel', 'debug'],
        scope: 'browser',
      },
      {
        id: 'reset',
        title: 'Reset to defaults',
        description: 'Restore this browser’s production settings to defaults.',
        keywords: ['reset', 'defaults', 'danger'],
        scope: 'browser',
      },
    ],
  },
];

export const DEFAULT_AREA: SettingsAreaId = 'locations';

export function getArea(id: string | null): SettingsArea {
  const fallback = SETTINGS_AREAS[0] as SettingsArea;
  return SETTINGS_AREAS.find((a) => a.id === id) ?? fallback;
}

/**
 * Legacy deep-link mapping. The pre-console page used `?tab=` (general |
 * qr-codes | labels | advanced) plus two hash families:
 * `#<general-section>` and `#<name>-accordion` (advanced). External surfaces
 * (Issues gear, Split Orders gear, bridge Slack alerts, Bridge Control) link
 * to these — keep them resolving to the new area + section anchor.
 */
const LEGACY_HASH_TO_TARGET: Record<string, { area: SettingsAreaId; section: string }> = {
  // General-tab hashes (`#section-*` ids / accordion values)
  'bench-routing': { area: 'orders', section: 'bench-routing' },
  'split-orders': { area: 'orders', section: 'split-orders' },
  'issue-tabs': { area: 'workflow', section: 'issue-tabs' },
  'shipping-provider': { area: 'locations', section: 'shipping-provider' },
  'print-technology': { area: 'locations', section: 'shipping-provider' },
  'print-file-self-heal': { area: 'printing', section: 'print-file-self-heal' },
  'designs-source': { area: 'printing', section: 'designs-source' },
  // `hybrid-fulfillment` was removed (deprecated no-op setting) — land on the
  // nearest surviving concern.
  'hybrid-fulfillment': { area: 'workflow', section: 'workflow-rules' },
  // Advanced-tab `#…-accordion` anchors
  'tencent-cos-accordion': { area: 'printing', section: 'tencent-cos' },
  'gcs-default-accordion': { area: 'printing', section: 'gcs' },
  'starshipit-accordion': { area: 'locations', section: 'starshipit' },
  'portless-accordion': { area: 'locations', section: 'portless' },
  'colour-codes-accordion': { area: 'printing', section: 'colour-codes' },
  'rip-routing-accordion': { area: 'printing', section: 'rip-routing' },
};

const LEGACY_TAB_TO_AREA: Record<string, SettingsAreaId> = {
  general: 'locations',
  'qr-codes': 'labels-qr',
  labels: 'labels-qr',
  advanced: 'system',
};

export function resolveLegacyLink(
  tab: string | null,
  hash: string,
): { area: SettingsAreaId; section: string | null } | null {
  const cleanHash = hash.replace(/^#/, '').replace(/^section-/, '');
  if (cleanHash && LEGACY_HASH_TO_TARGET[cleanHash]) {
    return { ...LEGACY_HASH_TO_TARGET[cleanHash], section: LEGACY_HASH_TO_TARGET[cleanHash].section };
  }
  // New-style deep link: `#<section-id>` for any registered section.
  if (cleanHash) {
    for (const area of SETTINGS_AREAS) {
      if (area.sections.some((s) => s.id === cleanHash)) {
        return { area: area.id, section: cleanHash };
      }
    }
  }
  if (tab && LEGACY_TAB_TO_AREA[tab]) {
    return { area: LEGACY_TAB_TO_AREA[tab], section: null };
  }
  return null;
}
